/**
 * Investor ROI helpers — pure-function math for IRR / NPV / payback / cashflow.
 * All figures in AUD unless noted.
 */

export function computeIRR(cashflows, guess = 0.1) {
  if (!cashflows || cashflows.length < 2) return NaN;
  // At least one negative and one positive required for a solvable IRR
  const hasNeg = cashflows.some((c) => c < 0);
  const hasPos = cashflows.some((c) => c > 0);
  if (!hasNeg || !hasPos) return NaN;

  // Newton-Raphson
  let rate = guess;
  const maxIter = 100;
  const tol = 1e-7;
  for (let iter = 0; iter < maxIter; iter++) {
    let npv = 0;
    let dnpv = 0;
    for (let i = 0; i < cashflows.length; i++) {
      const cf = cashflows[i];
      const denom = Math.pow(1 + rate, i);
      npv += cf / denom;
      if (i > 0) dnpv += (-i * cf) / Math.pow(1 + rate, i + 1);
    }
    if (Math.abs(npv) < tol) return rate;
    if (dnpv === 0 || !Number.isFinite(dnpv)) break;
    const nextRate = rate - npv / dnpv;
    if (!Number.isFinite(nextRate)) break;
    if (Math.abs(nextRate - rate) < tol) return nextRate;
    if (nextRate < -0.9999) {
      rate = -0.9999;
      break;
    }
    rate = nextRate;
  }

  // Bisection fallback
  let low = -0.99;
  let high = 5.0; // 500% IRR upper bound is plenty
  const npvAt = (r) => {
    let s = 0;
    for (let i = 0; i < cashflows.length; i++) s += cashflows[i] / Math.pow(1 + r, i);
    return s;
  };
  let fLow = npvAt(low);
  let fHigh = npvAt(high);
  if (fLow * fHigh > 0) return NaN;
  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    const fMid = npvAt(mid);
    if (Math.abs(fMid) < 1e-4) return mid;
    if (fLow * fMid < 0) {
      high = mid;
      fHigh = fMid;
    } else {
      low = mid;
      fLow = fMid;
    }
  }
  return (low + high) / 2;
}

export function computeNPV(cashflows, ratePct) {
  const r = (ratePct || 0) / 100;
  let s = 0;
  for (let i = 0; i < cashflows.length; i++) s += cashflows[i] / Math.pow(1 + r, i);
  return s;
}

/**
 * Build the annual cashflow rows and derived KPIs for the investor view.
 * inputs: {
 *   capital_cost, annual_maintenance_cost, maintenance_escalation_pct,
 *   discount_rate_pct, terminal_value_aud,
 *   estimated_energy_supplied_kwh, proposed_ppa_rate_cents,
 *   escalation_ppa_pct, degradation_pct_per_year, proposed_term_years
 * }
 */
export function buildInvestorProjection(p) {
  const capital = Number(p.capital_cost || 0);
  const opex0 = Number(p.annual_maintenance_cost || 0);
  const opexEsc = Number(p.maintenance_escalation_pct || 0) / 100;
  const discount = Number(p.discount_rate_pct || 10);
  const terminal = Number(p.terminal_value_aud || 0);

  const supplied0 = Number(p.estimated_energy_supplied_kwh || 0);
  const ppa0 = Number(p.proposed_ppa_rate_cents || 0) / 100; // $ / kWh
  const ppaEsc = Number(p.escalation_ppa_pct || 0) / 100;
  const degrade = Number(p.degradation_pct_per_year || 0) / 100;
  const term = Math.max(1, Math.round(Number(p.proposed_term_years || 10)));

  const rows = [];
  const cashflows = [-capital]; // year 0 outflow

  let cumCf = -capital;
  let totalRev = 0;
  let totalOpex = 0;
  let paybackYear = null;

  for (let i = 1; i <= term; i++) {
    const supplied = supplied0 * Math.pow(1 - degrade, i - 1);
    const ppa = ppa0 * Math.pow(1 + ppaEsc, i - 1);
    const revenue = supplied * ppa;
    const opex = opex0 * Math.pow(1 + opexEsc, i - 1);
    let netCf = revenue - opex;
    if (i === term && terminal) netCf += terminal;
    cashflows.push(netCf);
    cumCf += netCf;
    totalRev += revenue;
    totalOpex += opex;
    if (paybackYear == null && cumCf >= 0) paybackYear = i;
    rows.push({
      year: i,
      supplied_kwh: Math.round(supplied),
      ppa_rate_cents: Number((ppa * 100).toFixed(2)),
      revenue: Math.round(revenue),
      opex: Math.round(opex),
      net_cf: Math.round(netCf),
      cum_cf: Math.round(cumCf),
    });
  }

  const irr = computeIRR(cashflows, 0.1);
  const npv = computeNPV(cashflows, discount);

  const netProfit = totalRev - totalOpex - capital + terminal;
  const roiMultiple = capital > 0 ? (netProfit + capital) / capital : NaN;

  return {
    rows,
    cashflows,
    totals: {
      capital: Math.round(capital),
      terminal: Math.round(terminal),
      total_revenue: Math.round(totalRev),
      total_opex: Math.round(totalOpex),
      net_profit: Math.round(netProfit),
      roi_multiple: roiMultiple,
    },
    kpis: {
      irr,            // decimal, e.g. 0.145 = 14.5%
      npv,            // AUD
      payback_year: paybackYear,
      discount_rate: Number(p.discount_rate_pct || 10),
    },
  };
}
